
import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Avatar,
  Divider,
  Card,
  CardContent,
  Chip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Person as PersonIcon,
  Save as SaveIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import api from '../services/api';

function Profile() {
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({ nombre: '', email: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openPassword, setOpenPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    passwordActual: '',
    passwordNueva: '',
    passwordConfirmar: ''
  });

  useEffect(() => {
    cargarPerfil();
  }, []);

  const cargarPerfil = async () => {
    try {
      const response = await api.get('/auth/profile');
      const data = response.data.data;
      setUser(data);
      setFormData({ nombre: data.nombre, email: data.email });
    } catch (error) {
      setSnackbar({ open: true, message: 'Error cargando perfil', severity: 'error' });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    try {
      await api.put('/auth/profile', formData);
      setSnackbar({ open: true, message: 'Perfil actualizado correctamente', severity: 'success' });
      // Actualizar localStorage
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...storedUser, nombre: formData.nombre, email: formData.email }));
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Error al actualizar', severity: 'error' });
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleSubmitPassword = async () => {
    if (passwordData.passwordNueva !== passwordData.passwordConfirmar) {
      setSnackbar({ open: true, message: 'Las contraseñas no coinciden', severity: 'error' });
      return;
    }
    if (passwordData.passwordNueva.length < 6) {
      setSnackbar({ open: true, message: 'La contraseña debe tener al menos 6 caracteres', severity: 'error' });
      return;
    }
    try {
      await api.put('/auth/change-password', {
        passwordActual: passwordData.passwordActual,
        passwordNueva: passwordData.passwordNueva
      });
      setSnackbar({ open: true, message: 'Contraseña actualizada correctamente', severity: 'success' });
      setOpenPassword(false);
      setPasswordData({ passwordActual: '', passwordNueva: '', passwordConfirmar: '' });
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Error al cambiar contraseña', severity: 'error' });
    }
  };

  if (!user) return <Typography>Cargando...</Typography>;

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Mi Perfil
      </Typography>

      <Grid container spacing={3}>
        {/* Tarjeta de información */}
        <Grid item xs={12} md={4}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <CardContent>
              <Avatar sx={{ width: 100, height: 100, mx: 'auto', mb: 2, bgcolor: '#1976d2', fontSize: 40 }}>
                {getInitials(user.nombre)}
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {user.nombre}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                {user.email}
              </Typography>
              <Chip 
                label={user.rol.toUpperCase()} 
                color="primary" 
                size="small" 
                sx={{ mb: 2 }}
              />
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                <strong>Último acceso:</strong><br />
                {user.ultimo_acceso ? new Date(user.ultimo_acceso).toLocaleString('es-PE') : 'Nunca'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Miembro desde:</strong><br />
                {new Date(user.created_at).toLocaleDateString('es-PE')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Formulario de edición */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: '#1976d2' }}>
              ✏️ Editar Información
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nombre Completo"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Correo Electrónico"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                sx={{ backgroundColor: '#1976d2' }}
              >
                Guardar Cambios
              </Button>
              <Button
                variant="outlined"
                startIcon={<LockIcon />}
                onClick={() => setOpenPassword(true)}
                sx={{ color: '#d32f2f', borderColor: '#d32f2f' }}
              >
                Cambiar Contraseña
              </Button>
            </Box>
          </Paper>

          {/* Información adicional */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#388e3c' }}>
              ℹ️ Información de la Cuenta
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Rol:</strong> {user.rol === 'admin' ? 'Administrador' : user.rol === 'vendedor' ? 'Vendedor' : user.rol === 'tecnico' ? 'Técnico' : 'Gerente'}
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Estado:</strong> Activo
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Si necesitas cambiar tu rol o tienes problemas con tu cuenta, contacta al administrador del sistema.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Dialog Cambiar Contraseña */}
      <Dialog open={openPassword} onClose={() => setOpenPassword(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
          🔒 Cambiar Contraseña
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contraseña Actual"
                name="passwordActual"
                type="password"
                value={passwordData.passwordActual}
                onChange={handlePasswordChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nueva Contraseña"
                name="passwordNueva"
                type="password"
                value={passwordData.passwordNueva}
                onChange={handlePasswordChange}
                required
                helperText="Mínimo 6 caracteres"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirmar Nueva Contraseña"
                name="passwordConfirmar"
                type="password"
                value={passwordData.passwordConfirmar}
                onChange={handlePasswordChange}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPassword(false)} color="inherit">Cancelar</Button>
          <Button onClick={handleSubmitPassword} variant="contained" sx={{ backgroundColor: '#d32f2f' }}>
            Actualizar Contraseña
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Profile;